import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd } from '@angular/router';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { DateEx, WEEKDAYS } from '../../../functions/date-ex';
import { leaveLink } from '../../../functions/claim/leaveLink';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { TaskCountsResponse } from '../../../models/api/todo/task-count-response';
import { TaskRequest } from '../../../models/api/todo/task-request';
import { TaskResponse } from '../../../models/api/todo/task-response';
import { Action } from '../../../models/core/action';
import { TasksService } from '../../../services/tasks/tasks-service';
import { PersonalTaskDataService } from '../../../services/todo/personal-task-data.service';
import { BaseComponent } from '../../base/base.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { LoadingState } from '../../core/panels/loading-panel.component';
import { PanelComponent } from '../../core/panels/panel.component';
import { TaskSearchComponent } from './task-search.component';
import { TaskTableComponent } from './task-table.component';
import { TaskWeekComponent } from './task-week.component';
import { filter } from 'rxjs';

@Component({
  selector: 'app-task-view',
  imports: [
    TaskTableComponent,
    TaskWeekComponent,
    ReactiveFormsModule,
    PanelComponent,
    TaskSearchComponent,
    RadioButtonComponent,
  ],
  template: `
    <div class="">
      <app-radio-button
        [control]="tab"
        label="Task view"
        optionTitle="title"
        optionLabel="label"
        optionValue="key"
        [options]="tabs"
        [vertical]="false"
        (change)="onSelectRadio($event)"
      ></app-radio-button>
    </div>
    <div class="tw-py-6">
      <div class="tw-py-6" [hidden]="tab.value !== 'browse'">
        <app-task-week
          [items]="filteredCalendarItems"
          [claim]="claim"
          [loading]="tableLoading && !isClndrClicked"
          [isPageTodo]="isPageTodo"
          (includeCompletedChange)="onIncludeCompletedChange($event)"
          (calenderAction)="onCalenderAction($event)"
        />
      </div>
      <div class="tw-py-6" [hidden]="tab.value !== 'search'">
        <app-task-search
          (searchRequest)="searchRequest($event)"
          (clear)="onClearSearch()"
        />
      </div>
    </div>
    <h2 class="tw-text-base tw-flex tw-w-full">
      {{ activeTitle }}
    </h2>
    <hr />
    <app-panel>
      <app-task-table
        [dataSource]="filteredTaskData"
        [isPageTodo]="isPageTodo"
        [claim]="claim"
        [loading]="tableLoading"
        [error]="error"
        (searchCountEvent)="searchCountEvent($event)"
        (markTaskComplete)="markTaskComplete($event)"
      />
    </app-panel>
  `,
  styles: ``,
})
export class TaskViewComponent extends BaseComponent implements OnInit {
  private tasksService = inject(TasksService);
  private personalTaskData = inject(PersonalTaskDataService);

  @Input() claim: any = null;
  @Input() isPageTodo = false;
  @Output() search = new EventEmitter();
  @Output() clear = new EventEmitter();

  loading = false;
  tableLoading = false;
  error: any = null;
  activeTitle: string;
  selectedDate: any;
  searchCount: number;
  taskData: TaskResponse[] = [];
  taskRequest: TaskRequest;
  includeCompleted = false;
  calendarItems: any[] = [];
  weekStartDate: any;
  weekEndDate: any;
  formattedWeekRange: string;
  taskCount: TaskCountsResponse;
  taskResponseData: TaskResponse[];
  pastDue: boolean;
  InitilisingTasks: boolean;
  isClndrClicked = false;
  isMarkCompleteEventFired = false;
  cameFromTaskEdit = false;
  protected tab = new FormControl<string>('browse');
  protected state: LoadingState = 'Loading';
  protected tabs: Action[] = [
    { label: 'Browse', key: 'browse', title: 'Browse Tasks' },
    { label: 'Search', key: 'search', title: 'Search Tasks' },
  ];

  private _lastCalendarItems: any[] = [];
  private _lastIncludeCompletedCal = false;
  private _lastSelectedDate = '';
  private _lastFilteredCalendarItems: any[] = [];
  private _lastTaskData: TaskResponse[] = [];
  private _lastIncludeCompleted = false;
  private _lastTabMode = '';
  private _lastFilteredTaskTableData: any[] = [];

  constructor() {
    super();
    let previousUrl = this.router.url ?? '';
    this.dispose.subs = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(async (event) => {
        if (previousUrl.startsWith('/task-edit')) {
          await this.GetTaskEntries();
        }
        previousUrl = event.urlAfterRedirects ?? event.url;
      });
  }

  async ngOnInit() {
    if (this.cameFromTaskEdit) {
      this.loadInitialTasks();
    }
    this.isPageTodo = document.location.pathname === '/todo';
    this.state = 'Loaded';
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  @HandleLoadingState({ loadingProperty: 'tableLoading' })
  async loadInitialTasks() {
    this.claim = this.view.currentClaim();
    if (!this.taskRequest) {
      this.taskRequest = this.prepareRequest();
    }

    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.taskRequest, claimSpecificFields);
    }
    this.taskRequest.recordSource = this.claim?.recordSource;
    this.taskResponseData = await this.api.task.getTaskEntry(this.taskRequest);

    this.onTaskDataReceived(this.taskResponseData);
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  @HandleLoadingState({ loadingProperty: 'tableLoading' })
  async GetTaskEntries() {
    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      if (!this.taskRequest) {
        const ws = this.weekStartDate ?? DateEx.getStartOfWeek(new Date());
        this.taskRequest = this.prepareRequest(
          DateEx.clearTime(ws),
          DateEx.clearTime(
            this.weekEndDate ?? DateEx.getEndOfWeek(DateEx.getStartOfWeek(ws)),
          ),
        );
      }

      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.taskRequest, claimSpecificFields);
    }
    this.taskRequest.recordId = this.claim?.recordID || undefined;
    this.taskRequest.recordSource = this.claim?.recordSource || undefined;
    this.taskResponseData = await this.api.task.getTaskEntry(this.taskRequest);
    this.onTaskDataReceived(this.taskResponseData);
  }

  prepareCalenderDataSource() {
    const weekMap: any = {};
    for (let i = 0; i < 7; i++) {
      const date = new Date(this.weekStartDate);
      date.setDate(date.getDate() + i);
      const dayKey = WEEKDAYS[date.getDay()];
      weekMap[dayKey] = {
        day: dayKey,
        date: date.toISOString(),
        displayDate: DateEx.formatShortDate(date),
        completed: 0,
        pastDue: 0,
        draft: 0,
        open: 0,
      };
    }
    const today = new Date();
    const startOfWeek = new Date(this.weekStartDate);
    const endOfWeek = new Date(this.weekStartDate);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    for (const task of this.taskData) {
      const due = new Date(task.dateDue);

      if (!DateEx.dateBetween(due, startOfWeek, endOfWeek)) {
        continue; // Only count tasks in this week
      }

      const dayKey = WEEKDAYS[due.getDay()];
      const item = weekMap[dayKey];

      const itemDate = new Date(item.date);
      if (
        !item ||
        itemDate.getFullYear() !== due.getFullYear() ||
        itemDate.getMonth() !== due.getMonth() ||
        itemDate.getDate() !== due.getDate()
      ) {
        continue;
      }

      const status = (task.status || '').toLowerCase();
      if (status === 'completed') {
        item.completed++;
      } else if (status === 'open') {
        if (DateEx.dateBefore(due, today)) {
          item.pastDue++;
        } else {
          item.open++;
        }
      }
    }
    return Object.values(weekMap);
  }

  onTaskDataReceived(data: TaskResponse[]) {
    this.taskData = data;
    this.searchCount = data.length;

    if (this.tab.value === 'browse' && !this.isClndrClicked) {
      this.calendarItems = this.prepareCalenderDataSource();
    }

    this.isMarkCompleteEventFired = false;
    this.updateActiveTitle();

    this.tableLoading = false;
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async GetTasksCount() {
    this.taskCount = await this.api.task.getTaskCount();
  }

  activeItemDateEvent(dateStr: string) {
    this.selectedDate = dateStr;
    this.activeTitle = this.selectedDate;
  }

  searchRequest(request: TaskRequest) {
    this.tableLoading = true;
    this.taskRequest = request;
    this.GetTaskEntries();
  }

  onCalenderAction(event: {
    action: string;
    weekStartDate: Date;
    weekEndDate: Date;
    claimSpecificFields?: any;
  }) {
    this.tableLoading = true;
    this.selectedDate = DateEx.formatShortDate(event.weekStartDate);
    this.weekStartDate = DateEx.getStartOfWeek(event.weekStartDate);
    this.weekEndDate = DateEx.getEndOfWeek(this.weekStartDate);
    this.taskRequest = this.prepareRequest(
      event.weekStartDate,
      event.weekEndDate,
    );
    this.isClndrClicked = false;
    this.InitilisingTasks = false;
    switch (event.action) {
      case 'calendarClick': {
        this.isClndrClicked = true;
        const dateStr = this.selectedDate;
        this.activeItemDateEvent(dateStr);
        this.GetTaskEntries();
        break;
      }
      case 'previous':
      case 'next': {
        const dateStr = this.selectedDate;
        this.activeItemDateEvent(dateStr);
        this.GetTaskEntries();
        break;
      }
      case 'init': {
        this.InitilisingTasks = true;
        this.taskRequest = this.prepareRequest(
          event.weekStartDate,
          event.weekEndDate,
        );
        this.loadInitialTasks();
        this.activeItemDateEvent(this.selectedDate);
        break;
      }
      case 'today': {
        this.activeItemDateEvent(DateEx.formatShortDate(new Date()));
        this.GetTaskEntries();
        break;
      }
      default:
        break;
    }
  }

  getStatusLabel(status: string, dueDate: Date): string {
    const now = new Date();
    const due = new Date(dueDate);
    const normalizedStatus = status?.toLowerCase();

    if (normalizedStatus === 'completed') return 'Completed';
    if (normalizedStatus === 'draft') return 'Draft';
    if (DateEx.dateBefore(due, now) && normalizedStatus !== 'completed')
      return 'Past due';
    return 'Open';
  }

  searchEvent(event: string) {
    // TODO
  }

  searchCountEvent(event: number) {
    this.searchCount = event;
    this.updateActiveTitle();
  }

  @HandleLoadingState({
    loadingProperty: 'tableLoading',
    errorProperty: 'actionError',
  })
  async markTaskComplete($event: boolean) {
    this.isMarkCompleteEventFired = true;
    const data = this.storage.get('current-task');
    this.taskRequest = {
      taskUID: data.taskUid,
      completed: true,
      dueDate: data.dueDate,
      title: data.title,
      description: data.message,
      recordId: this.claim?.recordID || undefined,
      recordSource: this.claim?.recordSource || undefined,
    };
    if (data && this.taskRequest) {
      await this.api.task.updateTask(this.taskRequest);
      this.personalTaskData.notifyChanged();
      if (this.claim) {
        this.tasksService.deleteTasksByClaim({
          recordId: this.claim.recordId,
          recordSource: this.claim.recordSource,
        });
      }

      this.taskRequest = this.prepareRequest(
        this.weekStartDate,
        this.weekEndDate,
      );

      // Apply claim-specific filtering when not on the main todo page
      if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
        const claimSpecificFields = this.getClaimSpecificFields();
        Object.assign(this.taskRequest, claimSpecificFields);
      }

      this.taskResponseData = await this.api.task.getTaskEntry(
        this.taskRequest,
      );
      this.isClndrClicked = false;
      this.onTaskDataReceived(this.taskResponseData);
    }
  }

  prepareRequest(
    weekStartDate: any | null = null,
    weekEndDate: any | null = null,
  ): TaskRequest {
    return {
      title: '',
      taskStatus: 0,
      dueStartDate: DateEx.clearTime(weekStartDate),
      dueEndDate: DateEx.clearTime(weekEndDate),
      pastDue: false,
      recordSource: this.claim?.recordSource,
      recordId: this.claim?.recordID || undefined,
    };
  }

  onIncludeCompletedChange(value: boolean) {
    this.includeCompleted = value;
  }

  onClearSearch() {
    this.taskData = [];
    this.searchCount = 0;
    this.activeTitle = '0 Tasks';
    this.tableLoading = false;
  }

  onSelectRadio(event: any) {
    if (event.target.defaultValue === 'search') {
      const count = this.searchCount ?? 0;
      this.activeTitle = `${count} Task${count === 1 ? '' : 's'}`;
    } else {
      this.activeTitle =
        this.selectedDate ?? DateEx.formatShortDate(new Date());

      this.tableLoading = true;

      const currentDate = this.weekStartDate || new Date();
      const weekStart = DateEx.getStartOfWeek(currentDate);
      const weekEnd = DateEx.getEndOfWeek(weekStart);
      this.taskRequest = this.prepareRequest(
        DateEx.clearTime(weekStart),
        DateEx.clearTime(weekEnd),
      );
      this.GetTaskEntries();
    }
  }

  private updateActiveTitle() {
    if (this.tab.value === 'search') {
      const count = this.searchCount ?? 0;
      this.activeTitle = `${count} Task${count === 1 ? '' : 's'}`;
    } else {
      this.activeTitle = this.selectedDate ?? '';
    }
  }

  get filteredCalendarItems() {
    if (
      this._lastCalendarItems === this.calendarItems &&
      this._lastIncludeCompletedCal === this.includeCompleted &&
      this._lastSelectedDate === this.selectedDate
    ) {
      return this._lastFilteredCalendarItems;
    }

    const items = this.calendarItems.map((item) => {
      if (!this.includeCompleted) {
        return { ...item, completed: 0 };
      }
      return item;
    });
    if (
      (this.isClndrClicked || this.isMarkCompleteEventFired) &&
      this.selectedDate &&
      Array.isArray(items)
    ) {
      for (const item of items) {
        item.active =
          DateEx.formatShortDate(new Date(item.date)) === this.selectedDate;
      }
    }
    this._lastCalendarItems = this.calendarItems;
    this._lastIncludeCompletedCal = this.includeCompleted;
    this._lastSelectedDate = this.selectedDate;
    this._lastFilteredCalendarItems = items;
    return items;
  }

  get filteredTaskData() {
    if (
      this._lastTaskData === this.taskData &&
      this._lastIncludeCompleted === this.includeCompleted &&
      this._lastTabMode === this.tab.value
    ) {
      return this._lastFilteredTaskTableData;
    }
    const tasks =
      this.tab.value === 'search' || this.includeCompleted
        ? this.taskData
        : this.taskData.filter(
            (task) => (task.status || '').toLowerCase() !== 'completed',
          );

    this._lastTaskData = this.taskData;
    this._lastIncludeCompleted = this.includeCompleted;
    this._lastTabMode = this.tab.value;
    this._lastFilteredTaskTableData = tasks
      .map((task) => {
        let recordSource;
        let recordId;
        let fileNumber: any = null;

        switch (true) {
          case !!task.claimUID:
            recordSource = 'J';
            recordId = task.claimUID;
            break;
          case !!task.adaEventNumber:
            recordSource = 'A';
            recordId = task.adaEventNumber;
            break;
          case !!task.leaveNumber:
            recordSource = 'L';
            recordId = task.leaveNumber;
            break;
          case !!task.absenceUID:
            recordSource = 'T';
            recordId = task.absenceUID;
            fileNumber = task.absenceUID;
            break;
          case !!task.adaTamsRequestID:
            recordSource = 'R';
            recordId = task.adaTamsRequestID;
            fileNumber = task.adatamsEventNumber;
            break;
          default:
            recordSource = '';
            recordId = null;
            break;
        }

        return {
          title: task.title || 'Untitled',
          taskUid: task.taskUID,
          fileNumber: task.taskFor || fileNumber || '',
          firstName: task.firstName,
          lastName: task.lastName,
          dueDate: task.dateDue,
          status: this.getStatusLabel(task.status, task.dateDue),
          sendEmail: task.emailReminder,
          update: 'Update',
          message: task.description,
          navigatingRecID: task.recordID,
          recordSource,
          recordId,
          format: task.format,
        };
      })
      .filter((mappedTask) => {
        // Filter out "Past due" tasks when user searched for "Open" tasks (taskStatus: 1)
        if (this.tab.value === 'search' && this.taskRequest?.taskStatus === 1) {
          return mappedTask.status !== 'Past due';
        }

        // Filter past due tasks by current claim when in claim context
        if (
          !this.isPageTodo &&
          this.claim &&
          this.taskRequest?.pastDue === true &&
          this.tab.value === 'search'
        ) {
          return this.matchesCurrentClaim(mappedTask);
        }

        return true;
      });

    return this._lastFilteredTaskTableData;
  }

  private matchesCurrentClaim(mappedTask: any): boolean {
    if (!this.claim || !this.claim.recordSource) {
      return false;
    }

    if (leaveLink(this.claim.recordSource) && mappedTask.recordSource === 'L') {
      const claimLeave = numericAdaOrLeaveKeyFromClaim(this.claim);
      const taskLeave = numericAdaOrLeaveKeyFromClaim({
        fileNumber: mappedTask.recordId ?? mappedTask.fileNumber,
      });
      return (
        claimLeave != null &&
        taskLeave != null &&
        claimLeave === taskLeave
      );
    }

    // First, try to match by fileNumber if both task and claim have fileNumber
    const taskFileNumber = mappedTask.fileNumber;
    const claimFileNumber = this.claim.fileNumber;

    if (taskFileNumber && claimFileNumber) {
      return taskFileNumber === claimFileNumber;
    }

    // Fall back to recordSource and recordId comparison
    const taskRecordSource = mappedTask.recordSource;
    const taskRecordId = mappedTask.recordId?.toString();
    const claimRecordId = this.claim.recordId?.toString();

    return (
      taskRecordSource === this.claim.recordSource &&
      taskRecordId === claimRecordId
    );
  }

  private getClaimSpecificFields(): any {
    const claim = this.view.currentClaim();
    const z: TaskRequest = {
      claimUID: null,
      absenceUID: null,
      employeeUID: null,
      adaEventNumber: null,
      leaveNumber: null,
      adaTamsRequestID: null,
    };
    if (!claim) {
      return { ...z };
    }

    switch (claim.recordSource) {
      case 'J':
        return { ...z, claimUID: claim.recordId };
      case 'T':
        return {
          ...z,
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid ? Number(claim.hrMasterUid) : null,
        };
      case 'A':
        return {
          ...z,
          adaEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      case 'L':
        return {
          ...z,
          leaveNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      case 'R':
        return {
          ...z,
          adaTamsRequestID: claim.recordId,
        };
      default:
        return { ...z, claimUID: claim.recordId };
    }
  }
}
